import { Cta, SanityCtaProps } from "@/components/cta"
import { RichText, RichTextValue } from "@/components/rich-text"
import { SlideOnView } from "@/components/slide-on-view"
import SanityImageField from "@/integrations/sanity/components/SanityImageField"
import { ExclamationMarks } from "@/svgs/exclamation-marks"
import { useInView } from "react-intersection-observer"
import { SanityImageAsset, SanityReference } from "sanity-codegen"
import { motion } from "motion/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { cn } from "@/utils/cn"
import useEmblaCarousel from "embla-carousel-react"
import { useCallback, useEffect, useState, useRef } from "react"
import { useScroll, useTransform } from "motion/react"
import { EmblaCarouselType } from "node_modules/embla-carousel/esm/components/EmblaCarousel"
import { useIsMobile } from "@/ui/hooks/useIsMobile"

interface DevFriendlyProps {
  content: {
    images: {
      asset: SanityReference<SanityImageAsset>
    }[]
    title: RichTextValue
    description: string
    fixedSquareText?: string
    slides: {
      title: string
      subtitle: string
      cta: SanityCtaProps
    }[]
  }
}

function TwitchingImage({
  image,
  index,
  containerRef,
  isMobile,
}: {
  image: DevFriendlyProps["content"]["images"][number]
  index: number
  containerRef: React.RefObject<HTMLDivElement | null>
  isMobile: boolean
}) {
  if (!isMobile) {
    return <StaticImage image={image} index={index} />
  }

  return (
    <MobileTwitchingImage
      image={image}
      index={index}
      containerRef={containerRef}
    />
  )
}

function MobileTwitchingImage({
  image,
  index,
  containerRef,
}: {
  image: DevFriendlyProps["content"]["images"][number]
  index: number
  containerRef: React.RefObject<HTMLDivElement | null>
}) {
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  })

  // Each image gets a phase offset for wave effect
  const phaseOffset = index * 0.1

  // Map scroll to a sine wave for smooth twitching
  const rotate = useTransform(scrollYProgress, (progress) => {
    const wave = Math.sin((progress + phaseOffset) * Math.PI * 8) * 12
    return wave
  })

  const scale = useTransform(scrollYProgress, (progress) => {
    const wave = Math.sin((progress + phaseOffset) * Math.PI * 8)
    return 1 + Math.abs(wave) * 0.12
  })

  return (
    <div>
      <motion.div
        initial={{ scale: 2, opacity: 0 }}
        viewport={{ once: true }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{
          duration: 0.25,
          delay: index * 0.15,
          type: "spring",
        }}
        className="relative flex h-full w-full items-center justify-center border border-viola-500"
      >
        <motion.div
          style={{ rotate, scale }}
          whileHover={{
            rotate: [0, -5, 5, -5, 5, 0],
            scale: 1.05,
            transition: {
              rotate: {
                duration: 0.5,
                repeat: Infinity,
                repeatDelay: 0.2,
              },
              scale: {
                duration: 0.2,
              },
            },
          }}
        >
          <SanityImageField asset={image.asset} />
        </motion.div>
      </motion.div>
    </div>
  )
}

function StaticImage({
  image,
  index,
}: {
  image: DevFriendlyProps["content"]["images"][number]
  index: number
}) {
  return (
    <div>
      <motion.div
        initial={{ scale: 2, opacity: 0 }}
        viewport={{ once: true }}
        whileInView={{ scale: 1, opacity: 1 }}
        transition={{
          duration: 0.25,
          delay: index * 0.15,
          type: "spring",
        }}
        className="relative flex h-full w-full items-center justify-center border border-viola-500"
      >
        <motion.div
          whileHover={{
            rotate: [0, -5, 5, -5, 5, 0],
            scale: 1.05,
            transition: {
              rotate: {
                duration: 0.5,
                repeat: Infinity,
                repeatDelay: 0.2,
              },
              scale: {
                duration: 0.2,
              },
            },
          }}
        >
          <SanityImageField asset={image.asset} />
        </motion.div>
      </motion.div>
    </div>
  )
}

export function DevFriendly({ content }: DevFriendlyProps) {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })
  const imageGridRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()

  return (
    <div className="relative bg-primary-800 text-neutral-light md:py-20">
      <div
        ref={imageGridRef}
        className="mx-auto grid grid-cols-3 overflow-hidden md:grid-cols-5"
      >
        {content.images.map((image, index) => (
          <TwitchingImage
            key={index}
            image={image}
            index={index}
            containerRef={imageGridRef}
            isMobile={isMobile}
          />
        ))}
        <div className="h-full">
          <SanityImageField
            asset={content.images[0].asset}
            className="opacity-0"
          />
        </div>
        <div />
        <div className="border-r border-l border-viola-500" />
        <div className="border-r border-l border-viola-500" />
        <div className="border-r border-l border-viola-500" />
        <div
          className="relative col-span-3 row-span-2 p-4 md:col-span-2 md:pr-8 md:pl-16 lg:px-20"
          ref={ref}
        >
          <ExclamationMarks
            className="absolute -top-[4rem] right-0 -translate-y-3/4 md:top-[0] md:translate-x-1/2"
            delay={1}
            startAnimation={inView}
          />
          <SlideOnView delay={0.5}>
            <RichText value={content.title} />
          </SlideOnView>
          <SlideOnView className="mt-8 text-lg xl:text-xlg" delay={0.8}>
            {content.description}
          </SlideOnView>
        </div>
        <GridCarousel
          slides={content.slides}
          fixedSquareText={content.fixedSquareText}
          className="col-span-3"
        />
      </div>
    </div>
  )
}

function GridCarousel({
  slides,
  fixedSquareText,
  className,
}: {
  slides: DevFriendlyProps["content"]["slides"]
  fixedSquareText?: string
  className?: string
}) {
  const [emblaRefTitle, emblaApiTitle] = useEmblaCarousel({
    loop: true,
  })
  const [emblaRefSubtitle, emblaApiSubtitle] = useEmblaCarousel({
    loop: true,
  })
  const [emblaRefCta, emblaApiCta] = useEmblaCarousel({
    loop: true,
  })

  const [currentSlide, setCurrentSlide] = useState(0)
  const onSelect = useCallback(
    (emblaApi: EmblaCarouselType) => {
      const index = emblaApi.selectedScrollSnap()
      setCurrentSlide(index)
      emblaApiTitle?.scrollTo(index, false)
      emblaApiSubtitle?.scrollTo(index, false)
      emblaApiCta?.scrollTo(index, false)
    },
    [emblaApiTitle, emblaApiSubtitle, emblaApiCta]
  )

  const scrollNext = useCallback(() => {
    emblaApiTitle?.scrollNext()
  }, [emblaApiTitle])
  const scrollPrev = useCallback(() => {
    emblaApiTitle?.scrollPrev()
  }, [emblaApiTitle])

  useEffect(() => {
    if (!emblaApiTitle || !emblaApiSubtitle || !emblaApiCta) return
    onSelect(emblaApiTitle)
    emblaApiTitle.on("select", onSelect)
    emblaApiSubtitle.on("select", onSelect)
    emblaApiCta.on("select", onSelect)
  }, [emblaApiTitle, emblaApiSubtitle, emblaApiCta, onSelect])

  return (
    <div
      className={cn(
        "grid grid-cols-3 grid-rows-[auto_auto] divide-x divide-y divide-neutral-light border bg-viola-500 [&>div]:aspect-square",
        className
      )}
    >
      <div className="overflow-hidden" ref={emblaRefTitle}>
        <div className="flex h-full touch-pan-y touch-pinch-zoom">
          {slides.map((slide, index) => (
            <div
              key={index}
              className="flex flex-[0_0_100%] items-center justify-center px-4 md:px-8"
            >
              <p className="text-sm font-bold md:text-xl lg:text-2xl xl:text-3xl">
                {slide.title}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-[0_0_100%] items-center justify-center px-4 md:px-8">
        {fixedSquareText && (
          <p className="text-sm font-bold md:text-xl lg:text-2xl xl:text-3xl">
            {fixedSquareText}
          </p>
        )}
      </div>
      <div className="bg-primary-800"></div>
      <div className="grid grid-cols-2 grid-rows-2 items-center justify-items-center font-family-gtpressura text-3xl font-light md:text-4xl xl:text-7xl">
        <ChevronLeft
          onClick={scrollPrev}
          size={50}
          className="text-primary-500"
        />
        <p className="flex h-full w-full items-center justify-center bg-primary-500 text-green-500">
          {currentSlide + 1 < 10 ? `0${currentSlide + 1}` : currentSlide + 1}
        </p>
        <p className="flex h-full w-full items-center justify-center bg-primary-500 text-green-500">
          /{slides.length}
        </p>
        <ChevronRight
          onClick={scrollNext}
          size={50}
          className="text-primary-500"
        />
      </div>
      <div className="overflow-hidden" ref={emblaRefSubtitle}>
        <div className="flex h-full touch-pan-y touch-pinch-zoom">
          {slides.map((slide, index) => (
            <div
              key={index}
              className="flex flex-[0_0_100%] items-center justify-center"
            >
              <p className="px-4 text-sm font-bold md:px-8 md:text-xl lg:text-2xl xl:text-3xl">
                {slide.subtitle}
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="overflow-hidden" ref={emblaRefCta}>
        <div className="flex h-full touch-pan-y touch-pinch-zoom">
          {slides.map((slide, index) => (
            <div
              key={index}
              className="flex flex-[0_0_100%] items-center justify-center"
            >
              {slide.cta?.label && <Cta sanity={slide.cta} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
